/**
 * Dashboard query hook.
 * GET /api/dashboard — единый пульт.
 *
 * Контракт «что горит» (unclaimed_leads / no_next_step_deals / sla_flags / counts)
 * ЗАМОРОЖЕН — поля сохранены. Новые блоки (status_banner, money, funnel, clients,
 * tonya, trends) добавлены рядом. DEV-mock повторяет contract_json из спека, чтобы
 * DEV не падал без бэкенда; real-блоки считаются из БД, demo-блоки имеют is_demo:true.
 */

import { useQuery } from '@tanstack/react-query'
import { dashboardApi } from '@/api/client'
import type { Dashboard } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

// относительное время, чтобы пороги срочности (>30 мин = красный) были видны в DEV
const minutesAgo = (m: number) => new Date(Date.now() - m * 60 * 1000).toISOString()
const hoursAgo = (h: number) => new Date(Date.now() - h * 60 * 60 * 1000).toISOString()

const MOCK_DASHBOARD: Dashboard = {
  status_banner: {
    level: 'red',
    text: '1 сделка горит по SLA, 3 лида ждут взятия, 2 сделки без следующего шага',
  },

  unclaimed_leads: [
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000001',
      tenant_id: 'tenant-coffee',
      contact_id: 'c1000000-0000-0000-0000-000000000001',
      stage_key: 'new_lead',
      created_at: minutesAgo(42), // >30 мин → красный
      display_name: 'Кофейня «У дома»',
      claimed_by: null,
      source_channel: 'telegram',
    },
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000011',
      tenant_id: 'tenant-fitness',
      contact_id: 'c1000000-0000-0000-0000-000000000011',
      stage_key: 'new_lead',
      created_at: minutesAgo(14), // >10 мин → жёлтый
      display_name: 'Фитнес-студия «Импульс»',
      claimed_by: null,
      source_channel: 'web_form',
    },
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000012',
      tenant_id: 'tenant-flowers',
      contact_id: 'c1000000-0000-0000-0000-000000000012',
      stage_key: 'new_lead',
      created_at: minutesAgo(4),
      display_name: 'Цветы «Букет74»',
      claimed_by: null,
      source_channel: 'avito',
    },
  ],

  no_next_step_deals: [
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000002',
      tenant_id: 'tenant-nails',
      contact_id: 'c1000000-0000-0000-0000-000000000002',
      stage_key: 'conversation',
      title: 'Студия маникюра «Лак»',
      updated_at: hoursAgo(50),
      display_name: 'Студия маникюра «Лак»',
      stage_name: 'Разговор',
      value: 95000,
    },
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000004',
      tenant_id: 'tenant-dental',
      contact_id: 'c1000000-0000-0000-0000-000000000004',
      stage_key: 'proposal_sent',
      title: 'Стоматология «Улыбка»',
      updated_at: hoursAgo(30),
      display_name: 'Стоматология «Улыбка»',
      stage_name: 'КП отправлено',
      value: 140000,
    },
  ],

  sla_flags: [
    {
      deal_id: 'a1b2c3d4-0000-0000-0000-000000000003',
      tenant_id: 'tenant-garage',
      contact_id: 'c1000000-0000-0000-0000-000000000003',
      stage_key: 'proposal_sent',
      title: 'Автосервис «Гараж 24»',
      updated_at: hoursAgo(80),
      display_name: 'Автосервис «Гараж 24»',
      stage_name: 'КП отправлено',
      value: 120000,
      hours_idle: 80.0,
    },
  ],

  counts: {
    unclaimed_leads: 3,
    no_next_step_deals: 2,
    sla_flags: 1,
  },
  total_open_deals: 14,

  money: {
    pipeline_value: 870000,
    won_contracts_total: 1240000,
    prepaid_received: 690000,
    awaiting_second_tranche: 310000,
    avg_check: 96000,
    upsells_total: 145000,
    currency: 'RUB',
  },

  funnel: {
    stages: [
      { stage_key: 'new_lead', stage_name: 'Новый лид', count: 28, value: 0, drop_off: 0.32, is_bottleneck: false },
      { stage_key: 'conversation', stage_name: 'Разговор', count: 19, value: 1710000, drop_off: 0.21, is_bottleneck: false },
      { stage_key: 'proposal_sent', stage_name: 'КП отправлено', count: 15, value: 1440000, drop_off: 0.8, is_bottleneck: true },
      { stage_key: 'won', stage_name: 'Договор', count: 3, value: 288000, drop_off: 0.0, is_bottleneck: false },
    ],
    conversion_to_contract: 0.6,
    won_count: 9,
    lost_count: 6,
    lost_reasons: [
      { reason: 'Дорого', count: 3 },
      { reason: 'Не дозвонились', count: 2 },
      { reason: 'Выбрали другого', count: 1 },
    ],
  },

  clients: {
    by_phase: [
      { phase: 'delivery', label: 'Внедрение', count: 4 },
      { phase: 'hypercare', label: 'Гиперкэр', count: 2 },
      { phase: 'active', label: 'Активен', count: 7 },
      { phase: 'at_risk', label: 'Под угрозой', count: 1 },
    ],
    total_clients: 14,
    lead_sources: [
      { source_channel: 'telegram', count: 14 },
      { source_channel: 'web_form', count: 9 },
      { source_channel: 'avito', count: 4 },
      { source_channel: 'manual', count: 2 },
      { source_channel: 'max', count: 1 },
    ],
  },

  tonya: {
    is_demo: true,
    demo_label: 'Появится после подключения диалогов из ops-app',
    dialogs_today: 128,
    escalation_rate: 0.12,
    avg_response_time_s: 8,
    autoreply_rate: 0.88,
    csat: 4.6,
  },

  trends: {
    is_demo: true,
    demo_label: 'Появится после первых подписок и счётчика диалогов',
    mrr: 58800,
    active_subscriptions: 12,
    dialogs_billed_month: 1240,
    revenue_by_month: [
      { month: '2026-01', amount: 160000 },
      { month: '2026-02', amount: 240000 },
      { month: '2026-03', amount: 210000 },
      { month: '2026-04', amount: 320000 },
      { month: '2026-05', amount: 380000 },
      { month: '2026-06', amount: 290000 },
    ],
    leads_by_week: [
      { week: '2026-W22', count: 2 },
      { week: '2026-W23', count: 5 },
      { week: '2026-W24', count: 3 },
      { week: '2026-W25', count: 6 },
    ],
  },

  generated_at: new Date().toISOString(),
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
}

export function useDashboard() {
  return useQuery<Dashboard>({
    queryKey: dashboardKeys.all,
    queryFn: () => {
      if (USE_MOCK) return Promise.resolve(MOCK_DASHBOARD)
      return dashboardApi.get()
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
